import { apiClient } from '@/api/client';
import type {
  CreateOtherExpenseRequest,
  ExamPriceRow,
  ExamPricingHistoryResponse,
  ExamPricingResponse,
  ExamSection,
  FinancePeriodQuery,
  LedgerResponse,
  OtherExpenseCategory,
  OtherExpenseRow,
  PaginatedOtherExpenses,
  ReferralCostHistoryResponse,
  ReferralCostRow,
  ReferralCostsResponse,
  RevenueResponse,
  SchoolCostHistoryResponse,
  SchoolCostRow,
  SchoolCostsResponse,
  SetExamPriceRequest,
  SetReferralCostRequest,
  SetSchoolCostRequest,
  UpdateOtherExpenseRequest,
} from '@/types/shared';

/**
 * Finance API client (T125 config endpoints + T127 revenue/ledger/other-expenses) — typed to the
 * frozen `packages/shared/src/contracts/finance.ts` contract (T119/T122/T124). T125 covers the
 * exam-pricing + school-cost config endpoints consumed by `/settings/finance`
 * (`FinanceSettingsPage`); T127 adds `GET /finance/revenue`, `GET /finance/ledger`, and the
 * `/finance/expenses` CRUD consumed by `/finance` (`FinancePage`). All amount fields are sent as
 * decimal-as-STRING per the frozen contract (never a JS `number`) — the backend's global
 * `ValidationPipe` (`transformOptions.enableImplicitConversion: true`, `main.ts`) coerces the
 * numeric string to the DTO's `number` field before validation, exactly like the already-shipped
 * `setExamPrice`/`setSchoolCost` calls below.
 */

/** GET /finance/exam-pricing — all 4 sections in one call (dual-role: Admin + Accounts Officer;
 * this screen itself is Admin-only, §10.1, but the endpoint's own RBAC is broader). */
export function getExamPricing(): Promise<ExamPricingResponse> {
  return apiClient.get<ExamPricingResponse>('/finance/exam-pricing').then((res) => res.data);
}

/** GET /finance/exam-pricing/history — Admin-only. All versions across all 4 sections, newest-first. */
export function getExamPricingHistory(): Promise<ExamPricingHistoryResponse> {
  return apiClient
    .get<ExamPricingHistoryResponse>('/finance/exam-pricing/history')
    .then((res) => res.data);
}

/** PUT /finance/exam-pricing/{examSection} — Admin-only. Inserts a new effective-dated version
 * (never mutates history); `effectiveFrom` is omitted so the server stamps Cairo "now" (§4.3 v1
 * simplification — no forward-scheduling UI). Returns the new current `ExamPriceRow`. */
export function setExamPrice(
  examSection: ExamSection,
  payload: SetExamPriceRequest,
): Promise<ExamPriceRow> {
  return apiClient
    .put<ExamPriceRow>(`/finance/exam-pricing/${encodeURIComponent(examSection)}`, payload)
    .then((res) => res.data);
}

/** GET /finance/school-costs — one row per active agreed school (dual-role; screen is Admin-only). */
export function getSchoolCosts(): Promise<SchoolCostsResponse> {
  return apiClient.get<SchoolCostsResponse>('/finance/school-costs').then((res) => res.data);
}

/** GET /finance/school-costs/history — Admin-only. All versions across all schools, newest-first. */
export function getSchoolCostHistory(): Promise<SchoolCostHistoryResponse> {
  return apiClient
    .get<SchoolCostHistoryResponse>('/finance/school-costs/history')
    .then((res) => res.data);
}

/** PUT /finance/school-costs/{schoolId} — Admin-only. Inserts a new effective-dated version (GAP-2:
 * recognized once per student at enrollment, never retroactive); `effectiveFrom` omitted (v1
 * immediate-only, §5.2). Returns the new current `SchoolCostRow`. */
export function setSchoolCost(
  schoolId: number,
  payload: SetSchoolCostRequest,
): Promise<SchoolCostRow> {
  return apiClient
    .put<SchoolCostRow>(`/finance/school-costs/${schoolId}`, payload)
    .then((res) => res.data);
}

/* ===========================================================================================
 * T151 — Referral Costs tab (`/settings/finance`, 3rd tab) — `docs/architecture/
 * 16-referral-partners.md` §4/§7, endpoints 5–7. A faithful mirror of the school-cost config
 * calls above (same batching/versioning/no-backdating rules, `partner` in place of `school`).
 * =========================================================================================== */

/** GET /finance/referral-costs — one row per active referral partner (dual-role: Admin +
 * Accounts Officer; the `/settings/finance` screen itself is Admin-only, same precedent as
 * `getSchoolCosts`). `costAmount` is `null` for a partner never configured — never `0`. */
export function getReferralCosts(): Promise<ReferralCostsResponse> {
  return apiClient.get<ReferralCostsResponse>('/finance/referral-costs').then((res) => res.data);
}

/** GET /finance/referral-costs/history — Admin-only. Every version across every partner,
 * newest-first per partner, `isCurrent` flagged. */
export function getReferralCostHistory(): Promise<ReferralCostHistoryResponse> {
  return apiClient
    .get<ReferralCostHistoryResponse>('/finance/referral-costs/history')
    .then((res) => res.data);
}

/** PUT /finance/referral-costs/{partnerId} — Admin-only. Inserts a new effective-dated version
 * (append-only, DEC-REF2); `effectiveFrom` omitted so the server stamps Cairo "now" (mirrors
 * `setSchoolCost` — no forward-scheduling UI in v1). NEVER touches any student's already-
 * recognized cost (requirements §5 Rule 4) — this is a pure config write. 404 if `partnerId` is
 * unknown; 422 `PRICING_EFFECTIVE_DATE_IN_PAST` on backdating. Returns the new current
 * `ReferralCostRow`. */
export function setReferralCost(
  partnerId: number,
  payload: SetReferralCostRequest,
): Promise<ReferralCostRow> {
  return apiClient
    .put<ReferralCostRow>(`/finance/referral-costs/${partnerId}`, payload)
    .then((res) => res.data);
}

/* ===========================================================================================
 * T127 — `/finance` page: revenue summary, money-flow ledger, other-expenses CRUD.
 * =========================================================================================== */

/** GET /finance/revenue?period=&from=&to= — income/expenses/net totals over the resolved Cairo
 * period (17-finance-ux.md §3.3). Dual-role (Admin + Accounts Officer). */
export function getRevenue(params: FinancePeriodQuery): Promise<RevenueResponse> {
  return apiClient.get<RevenueResponse>('/finance/revenue', { params }).then((res) => res.data);
}

/** GET /finance/ledger?period=&from=&to= — the derived-UNION money-flow ledger, newest-first
 * (17-finance-ux.md §3.4/§3.5). Dual-role (Admin + Accounts Officer). Same resolved-period params
 * as `getRevenue` — the two are always fetched together so they never visually disagree (AC11). */
export function getLedger(params: FinancePeriodQuery): Promise<LedgerResponse> {
  return apiClient.get<LedgerResponse>('/finance/ledger', { params }).then((res) => res.data);
}

export interface ListOtherExpensesParams {
  page?: number;
  pageSize?: number;
  category?: OtherExpenseCategory;
  /** Inclusive Cairo `YYYY-MM-DD` bounds on `expense_date` — independent of the revenue/ledger
   * `period` resolver (a simple list filter, §3.6). */
  from?: string;
  to?: string;
}

/** GET /finance/expenses — paged list, category + date-range filters (17-finance-ux.md §3.6).
 * Admin + Accounts Officer. */
export function listOtherExpenses(
  params: ListOtherExpensesParams,
): Promise<PaginatedOtherExpenses> {
  return apiClient
    .get<PaginatedOtherExpenses>('/finance/expenses', { params })
    .then((res) => res.data);
}

/** POST /finance/expenses — Admin + Accounts Officer. */
export function createOtherExpense(payload: CreateOtherExpenseRequest): Promise<OtherExpenseRow> {
  return apiClient.post<OtherExpenseRow>('/finance/expenses', payload).then((res) => res.data);
}

/** PATCH /finance/expenses/{id} — partial edit; 422 EXPENSE_ALREADY_DELETED if already
 * soft-deleted. */
export function updateOtherExpense(
  id: string,
  payload: UpdateOtherExpenseRequest,
): Promise<OtherExpenseRow> {
  return apiClient
    .patch<OtherExpenseRow>(`/finance/expenses/${encodeURIComponent(id)}`, payload)
    .then((res) => res.data);
}

/** DELETE /finance/expenses/{id} — soft-delete (never hard-deleted); 422
 * EXPENSE_ALREADY_DELETED if already soft-deleted (no double-delete). */
export function deleteOtherExpense(id: string): Promise<void> {
  return apiClient
    .delete<void>(`/finance/expenses/${encodeURIComponent(id)}`)
    .then(() => undefined);
}
